import type { PrinterDto } from "./printers";

/**
 * Building the options for a printer dropdown.
 *
 * Deliberately a module of its own rather than part of `printers.ts`: this is a pure function,
 * but the printer *client* is mocked wholesale (`vi.mock("@/lib/printers", () => …)`) by every
 * test of a component that fetches the list. Anything living alongside `printersApi` disappears
 * with it — for a re-exported binding that is not even a runtime failure, it is a module that
 * fails to link. Keeping the helper here means mocking the client never silently removes it.
 */

export type PrinterOption = Pick<PrinterDto, "printerId" | "printerName">;

/**
 * The shape of a printer-list fetch, whether it came from react-query or a hand-rolled effect.
 *
 * Deliberately only the two fields this module branches on: `data === undefined` already means
 * "nothing to resolve against", so a separate isLoading would be a third way to say the same thing
 * and a fourth state to keep consistent. Callers with a react-query result can pass it straight in.
 */
export interface PrintersQueryState {
  data: PrinterDto[] | undefined;
  isError: boolean;
}

// The filtered dropdown is UI-only guidance — the backend accepts any existing printer id
// regardless of type. If a stored id was set before this filter existed (or the printer's type
// flags changed since), it may not be in the filtered list. Rather than the control silently
// showing "— None —" while state still holds the real id — which would let the user save over, or
// print to, a value they were never shown — surface the actual printer (marked as not matching
// this slot's type), or an "unavailable" marker if the id no longer resolves at all.
// This must hold up while the printer list is still loading or has failed to load too: those
// states must never fall through to reporting the same "no value" the null case does, or a
// transient (or permanent, if the fetch keeps failing) loading state would silently look
// identical to "this employee has no default" for as long as it lasts.
//
// Callers that pass the chosen id on to something real — an agent queue name, say — must resolve
// that name from the printer list, NOT from the option returned here: the decorated
// "Zebra C (not a label printer)" is display text and matches no queue on any workstation.
export function withCurrentValue(
  filtered: PrinterOption[],
  printersQ: PrintersQueryState,
  currentId: number | null,
  notAppropriateLabel: string,
): PrinterOption[] {
  if (currentId == null) return filtered;
  if (filtered.some((p) => p.printerId === currentId)) return filtered;
  const allPrinters = printersQ.data;
  if (allPrinters === undefined) {
    const placeholder = printersQ.isError
      ? `Printer #${currentId} (printer list failed to load)`
      : "Loading…";
    return [...filtered, { printerId: currentId, printerName: placeholder }];
  }
  const match = allPrinters.find((p) => p.printerId === currentId);
  const extra: PrinterOption = match
    ? { printerId: currentId, printerName: `${match.printerName} (${notAppropriateLabel})` }
    : { printerId: currentId, printerName: `Printer #${currentId} (unavailable)` };
  return [...filtered, extra];
}
